import { useQueries } from '@tanstack/react-query';
import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import { getCryptoAccountNumbers } from 'utils/accounts/getCryptoAccountNumbers';
import { hasValue } from 'utils/hasValue';

import { useGetCollectionFundingInstructions } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

import {
  getGetCryptoEligibleVbasQueryOptions,
  useGetCryptoEligibleVbas,
  useGetCryptoExchangeFromCurrencies
} from 'api/customerApi';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useCryptoProvider } from 'hooks/useCryptoProvider';
import type { CryptoProvider } from 'hooks/useCryptoProvider';

type VirtualBankAccountContextValue = {
  cryptoProvider?: CryptoProvider;
  isC2c: boolean;
  virtualAccountNumber: string | undefined;
  setVirtualAccountNumber: Dispatch<string>;
  accounts: VirtualAccount[];
  isAccountsLoading: boolean;
};

const VirtualBankAccountContext = createContext<VirtualBankAccountContextValue>(
  {
    cryptoProvider: undefined,
    isC2c: false,
    virtualAccountNumber: undefined,
    setVirtualAccountNumber: () => undefined,
    accounts: [],
    isAccountsLoading: false
  }
);

const LAYER1_ACCOUNT_SELECTOR_STALE_TIME = 5 * 60 * 1000;

type Props = {
  children: ReactNode;
};

export const VirtualBankAccountProvider = ({ children }: Props) => {
  const organisationId = useOrganisationId<number>();
  const { cryptoProvider, isC2c } = useCryptoProvider();
  const isLayer1 = cryptoProvider === 'layer1';

  const [virtualAccountNumber, setVirtualAccountNumber] = useState<string>();

  const {
    data: { data: fundingInstructions } = {},
    isLoading: isFundingInstructionsLoading
  } = useGetCollectionFundingInstructions(organisationId, {
    query: { enabled: isC2c }
  });
  const { accounts: virtualAccounts } = useVirtualAccounts({
    filterSuspendedAccounts: true,
    filterTerminatedAccounts: true,
    filterCryptoAccounts: true
  });

  const {
    data: layer1ExchangeFromCurrencies,
    isLoading: isLayer1ExchangeFromCurrenciesLoading
  } = useGetCryptoExchangeFromCurrencies(organisationId, undefined, {
    query: {
      enabled: isLayer1,
      staleTime: LAYER1_ACCOUNT_SELECTOR_STALE_TIME,
      refetchOnWindowFocus: false
    }
  });

  const layer1ExchangeAssets = useMemo(
    () => [
      ...new Set(
        (layer1ExchangeFromCurrencies?.data?.currencies ?? [])
          .map((currency) => currency.asset)
          .filter(hasValue)
      )
    ],
    [layer1ExchangeFromCurrencies?.data?.currencies]
  );

  const layer1AccountQueries = useQueries({
    queries: layer1ExchangeAssets.map((asset) =>
      getGetCryptoEligibleVbasQueryOptions(
        organisationId,
        { asset },
        {
          query: {
            enabled: isLayer1,
            staleTime: LAYER1_ACCOUNT_SELECTOR_STALE_TIME,
            refetchOnWindowFocus: false
          }
        }
      )
    )
  });

  const layer1AccountNumbers = useMemo(() => {
    const numbers = new Set<string>();

    layer1AccountQueries.forEach((query) => {
      (query.data?.data?.vbas ?? []).forEach((accountNumber) => {
        if (hasValue(accountNumber)) numbers.add(accountNumber);
      });
    });

    return numbers;
  }, [layer1AccountQueries]);

  const accounts = useMemo(() => {
    if (!hasValue(virtualAccounts)) return [];

    if (isLayer1) {
      return virtualAccounts.filter((account) =>
        layer1AccountNumbers.has(account.account_number)
      );
    }

    if (!isC2c || !hasValue(fundingInstructions)) return [];

    const cryptoAccountNumbers = getCryptoAccountNumbers(fundingInstructions);

    return virtualAccounts.filter((account) =>
      cryptoAccountNumbers.has(account.account_number)
    );
  }, [
    isLayer1,
    layer1AccountNumbers,
    isC2c,
    fundingInstructions,
    virtualAccounts
  ]);

  // Without an asset the backend resolves the account the widget opens on
  // across the whole selector, so the user lands on a ready-to-use panel.
  const { data: defaultVbaResponse, isLoading: isDefaultVbaLoading } =
    useGetCryptoEligibleVbas(organisationId, undefined, {
      query: {
        enabled: isLayer1 || isC2c,
        staleTime: LAYER1_ACCOUNT_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    });

  const defaultVirtualAccountNumber = useMemo(() => {
    const defaultVba = defaultVbaResponse?.data?.default_vba;

    return accounts.some((account) => account.account_number === defaultVba)
      ? (defaultVba ?? undefined)
      : undefined;
  }, [accounts, defaultVbaResponse?.data?.default_vba]);

  const selectedVirtualAccountNumber =
    virtualAccountNumber ?? defaultVirtualAccountNumber;

  const isLayer1AccountsLoading =
    isLayer1ExchangeFromCurrenciesLoading ||
    layer1AccountQueries.some((query) => query.isLoading);

  const value = useMemo(
    () => ({
      cryptoProvider,
      isC2c,
      virtualAccountNumber: selectedVirtualAccountNumber,
      setVirtualAccountNumber,
      accounts,
      isAccountsLoading:
        isDefaultVbaLoading ||
        (isC2c ? isFundingInstructionsLoading : isLayer1AccountsLoading)
    }),
    [
      cryptoProvider,
      isC2c,
      selectedVirtualAccountNumber,
      accounts,
      isDefaultVbaLoading,
      isFundingInstructionsLoading,
      isLayer1AccountsLoading
    ]
  );

  return (
    <VirtualBankAccountContext.Provider value={value}>
      {children}
    </VirtualBankAccountContext.Provider>
  );
};

export const useVirtualBankAccount = () =>
  useContext(VirtualBankAccountContext);
